"use client";

import { fetchUserAttributes } from "aws-amplify/auth";

// Step 7 commit 13 / AC227-AC228: client-side reader for the Step 7
// payment-method stub seam. Step 8 swaps these custom attrs for real
// QuickBooks customer lookups (token / saved-method introspection);
// the same UserPaymentState shape stays so consumers do not change.

export type SavedPaymentMethodType = "Card" | "Bank" | "PayPal" | "Affirm";

export type UserPaymentState = {
  paidOrderCount: number;
  savedMethod: {
    type: SavedPaymentMethodType;
    last4: string;
  } | null;
  classification: "new" | "active-no-saved" | "active-with-saved";
};

function asMethodType(raw: string | undefined): SavedPaymentMethodType | null {
  if (!raw) return null;
  const normalized = raw.trim();
  if (
    normalized === "Card" ||
    normalized === "Bank" ||
    normalized === "PayPal" ||
    normalized === "Affirm"
  ) {
    return normalized;
  }
  return null;
}

export function classifyUserState(
  attrs: Partial<Record<string, string>>,
): UserPaymentState {
  const paidOrderCountRaw = attrs["custom:paidOrderCount"];
  const paidOrderCount = paidOrderCountRaw
    ? Math.max(0, Math.floor(Number(paidOrderCountRaw)) || 0)
    : 0;
  const last4 = (attrs["custom:paymentMethodLast4"] ?? "").trim();
  const type = asMethodType(attrs["custom:paymentMethodType"]);
  const savedMethod =
    type && last4 && /^\d{4}$/.test(last4) ? { type, last4 } : null;
  let classification: UserPaymentState["classification"];
  if (paidOrderCount === 0) classification = "new";
  else if (!savedMethod) classification = "active-no-saved";
  else classification = "active-with-saved";
  return { paidOrderCount, savedMethod, classification };
}

export async function fetchUserPaymentState(): Promise<UserPaymentState> {
  const attrs = await fetchUserAttributes();
  return classifyUserState(attrs);
}
